import { useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import Typography from 'components/Typography';

import { useConversation } from 'hooks/conversation';
import { useAnalytics } from 'hooks/useAnalytics';

import { ANALYTICS } from 'analytics/constants';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { isLastMessage } from 'utils/twilio';
import {
  StyledContentWrapper,
  StyledQuickRepliesTitle,
  StyledQuickRepliesWrapper,
  StyledQuickReplyOption,
} from './styles';
import { QuickRepliesComponentProps, QuickReplyType } from './types';
import { htmlId } from 'utils/html';
import { AnalyticQuickReplyDetailsType } from 'analytics/types';

const QuickReplies = ({ content, twilioMessageIndex }: QuickRepliesComponentProps) => {
  const { conversationState } = useConversation();
  const { showBoundary } = useErrorBoundary();

  const [disabled, setDisabled] = useState(false);

  const twilioConversationLength = conversationState.messages.length;
  const isLatestMessage = isLastMessage(twilioMessageIndex, twilioConversationLength);

  const { trackEvent } = useAnalytics();

  const onClickQuickReply = async (quickReply: QuickReplyType) => {
    setDisabled(true);
    try {
      // NOTE: AI Engine expects and depends on this payload property to render forms, etc. Very trivial.
      await window[CHATUI_WINDOW_PATH].sendMessage(quickReply.title, quickReply.payload);
    } catch (e: any) {
      return showBoundary(e);
    }
    // Aligned with backend analytics event. Ref: https://docs.google.com/document/d/1PX58MamIZS-plb_O_Z3uPHhJNqipNfbhmQemFawQk9c/edit
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.QUICK_REPLY,
      details: {
        intent_id: quickReply.payload.quick_reply_intent.intent_id,
        intent_class_id: quickReply.payload.quick_reply_intent.intent_class_id,
        quick_reply_text: quickReply.title,
        quick_reply_source: quickReply.payload.source,
        quick_reply_uid: quickReply.payload.uid,
      } as AnalyticQuickReplyDetailsType,
    });
  };

  // hide previous Quick Replies content. Only show if latest message.
  if (!isLatestMessage) {
    return null;
  }

  return (
    <StyledQuickRepliesWrapper id={htmlId('quick-replies-container')}>
      <StyledQuickRepliesTitle id={htmlId('quick-replies-title')}>
        {content.title && (
          <Typography as='h2' color='strong' variant='heading' size='sm'>
            {content.title}
          </Typography>
        )}
      </StyledQuickRepliesTitle>

      <StyledContentWrapper id={htmlId('quick-replies-content-container')}>
        {content.quickReplies.map((quickReply) => (
          <StyledQuickReplyOption
            id={htmlId(`quick-replies-option-${quickReply.id}`)}
            key={quickReply.id}
            disabled={disabled}
            onClick={() => onClickQuickReply(quickReply)}
          >
            {quickReply.title}
          </StyledQuickReplyOption>
        ))}
      </StyledContentWrapper>
    </StyledQuickRepliesWrapper>
  );
};

export default QuickReplies;
